import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';

import { ContentCardModule } from '@app/library/content-card/content-card.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { ScreenService } from '@core/services';
import { SpinnersModule } from 'lingo2-forms';
import { Content, ContentTypeEnum, EContentPaneFormat } from 'lingo2-models';
import { InViewportModule } from 'ng-in-viewport';
import { takeUntil } from 'rxjs/operators';
import { i18nService as TranslateService } from '../../../i18n/translate.service';
import { AbstractContentPane } from '../models/abstract-content-pane';

@Component({
  selector: 'app-card-universal',
  standalone: true,
  imports: [
    LocaleSharedModule,
    CommonModule,
    RouterModule,

    SpinnersModule,
    LibraryElementsModule,
    ModalModule,
    MobileModalModule,
    InViewportModule,
    ContentCardModule,
  ],
  templateUrl: './card-universal.component.html',
  styleUrls: ['./card-universal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardUniversalComponent extends AbstractContentPane implements OnInit, OnDestroy {
  private screenService = inject(ScreenService);
  protected router = inject(Router);
  protected location = inject(Location);
  protected translate = inject(TranslateService);

  public showModal = false;
  public item: Content = null;
  public isSmallScreen = false;
  public infinityFormat: EContentPaneFormat = EContentPaneFormat['card-mini'];
  private originalUrl: string;

  public ngOnInit() {
    this.screenService.width$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.isSmallScreen = this.screenService.isSmallScreen;
      },
      error: (error) => {
        this.isSmallScreen = false;
        this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
      },
    });
  }

  protected onContentLoaded() {
    this.infinityFormat = this.initInfinityFormat();
    this.isVisible = !!this.content.length;
  }

  private initInfinityFormat(): EContentPaneFormat {
    if (this.pane && !this.pane.infinity) {
      return;
    }

    let contentType: ContentTypeEnum;
    if (this.pane.contentFilter.content_type) {
      contentType = this.pane.contentFilter.content_type[0] || this.pane.contentFilter.content_type;
    }

    switch (contentType) {
      case ContentTypeEnum.video:
      case ContentTypeEnum.video_lesson:
        return EContentPaneFormat['card-mid'];
      case ContentTypeEnum.theory:
        return EContentPaneFormat['card-mini'];
      case ContentTypeEnum.article:
      case ContentTypeEnum.news:
      case ContentTypeEnum.audio:
      case ContentTypeEnum.audio_lesson:
        return EContentPaneFormat['card-micro'];
      default:
        return EContentPaneFormat['card-mini'];
    }
  }

  public onOpenCard(content: Content) {
    this.item = content;
    this.showModal = true;
    const url = this.contentRoute(this.item);
    const currentUrl = this.location.path(false);
    if (currentUrl !== url) {
      this.originalUrl = currentUrl;
      this.location.replaceState(url);

      // отправка событий в аналитику для имитации навигации по страницам
      this.analytics.pageView(url, currentUrl);
    }
    this.markForCheck();
  }

  public onCloseModal(e) {
    this.showModal = !e;
    if (this.originalUrl) {
      this.location.replaceState(this.originalUrl);
    }
    this.markForCheck();
  }

  public onInViewportBottomChange(reached) {
    // logger.debug('CardUniversalComponent::', reached);
    if (reached.visible) {
      this.loadNextPage();
    }
  }

  public trackByFn(index) {
    return index;
  }

  protected readonly EContentPaneFormat = EContentPaneFormat;
}
